/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useMemo } from 'react';
import { Card, CardContent, Box, Paper, Typography, InputAdornment, Select, MenuItem, FormControl, FormControlLabel, Checkbox } from '@mui/material';
import Input from '../../common/form/input';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { Button } from '@mui/material';
import { AUTH_PRODUCT_TYPE_ME, COOKIE_KEYS, COUNTRY_CODES, DEALER_STATUS, DEFAULT_COUNTRY_CODE, TOAST_OPTIONS, WEB_VIEW_EVENTS } from '../../../constants/app-constants';
import saveCookie from '../../../utils/helpers/save-cookie';
import LoginOtp from './loginOtp';
import { useEffect } from 'react';
import { mobileNumberValidatorAE } from '../../../utils/validation/validateMobile';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATH } from '../../../constants/route-constants';
import PageLoader from '../../common/page-loader';
import deleteCookie from '../../../utils/helpers/delete-cookie';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { ConfirmationDialog } from '../../common/modal';
import { sendWebviewEvent } from '../../../utils/helpers/webview-event';
import { trackDealerEvents } from '../../../tracking';
import { clearItem } from '../../../utils/helpers/storage-crud';

const getTrimmedNumber = (num = '') => {
    return num && num.indexOf('0') === 0 ? num.replace(/^0+/, '') : num;
};

const MobileLogin = ({ sendOtpConnect, verifyOtpConnect, setupToastConnect, enlistDealersConnect, setUserConnect, getUserConsentConnect }) => {
    const [showOtp, setShowOtp] = useState(false);
    const [whatsappOptIn, setWhatsappOptIn] = useState(true);
    const [mobile, setMobile] = useState('');
    const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
    const [otpError, setOtpError] = useState(null);
    const [enableSendOtp, setEnableSendOtp] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (mobileNumberValidatorAE(mobile, true)) {
            setEnableSendOtp(true);
            trackDealerEvents('enterMobileNumber', { phone: mobile });
        } else if (enableSendOtp) setEnableSendOtp(false);
    }, [mobile]);

    useEffect(() => {
        trackDealerEvents('loginPage');
    }, []);

    const selectedCountry = useMemo(() => {
        return COUNTRY_CODES.find(c => c.code === countryCode)?.initial || 'AE';
    }, [countryCode]);

    const handleUserConsent = () => {
        setIsLoading(true);
        setShowConfirmation(false);
        const consentPayload = { uid: getTrimmedNumber(mobile), optIn: whatsappOptIn, channel: 'WHATSAPP' };
        getUserConsentConnect(consentPayload, selectedCountry).then(() => {
            handleGetOtp();
        }).catch((error) => {
            setupToastConnect({ message: error?.error || 'Something went wrong' });
            setIsLoading(false);
        });
    };

    const handleLogin = () => {
        if (!whatsappOptIn) {
            setShowConfirmation(true);
            return;
        }
        trackDealerEvents('getOtp', { phone: mobile });
        handleUserConsent();
    };
    const handleGetOtp = (isResend) => {
        if (!isLoading) setIsLoading(true);
        sendOtpConnect(getTrimmedNumber(mobile), selectedCountry).then(() => {
            setShowOtp(true);
            if (isResend) {
                setupToastConnect({ message: 'Resent OTP successfully!', type: TOAST_OPTIONS.SUCCESS });
            }
        }).catch((error) => {
            setupToastConnect({ message: error?.message || 'Something went wrong' });
        }).finally(() => {
            setIsLoading(false);
            if (isResend && otpError) {
                setOtpError(false);
            }
        });
    };

    const handleChange = (event) => {
        const { value } = event?.target || {};
        setMobile(value);
    };

    const removeCookie = () => {
        deleteCookie(COOKIE_KEYS.ACCESS_TOKEN);
        clearItem(COOKIE_KEYS.CALLBACK_REQUESTES);
    };

    const handleDealerManagement = (phoneNumber) => {
        enlistDealersConnect({ phoneNumber }).then(dealer => {
            if (dealer?.status !== DEALER_STATUS.BANNED) {
                setUserConnect(dealer);
                navigate(ROUTE_PATH.HOME);
            } else {
                setupToastConnect({ message: 'Your id is de-activated for some reason, please contact your RM.' });
                removeCookie();
            }
            const country = COUNTRY_CODES.find(country => country.initial === dealer?.countryCode);
            const dealerDetails = { ...dealer, contactPerson: { ...dealer?.contactPerson, primaryMobileNumber: `${country?.code}${dealer?.contactPerson?.primaryMobileNumber}` } };
            sendWebviewEvent({ token: dealerDetails, type: WEB_VIEW_EVENTS.DEALER_DATA });
        }).catch(error => {
            setupToastConnect({ message: error?.message || 'Something went wrong!, please contact your RM.' });
            removeCookie();
        }).finally(() => setIsLoading(false));
    };

    const handleVerify = (otp) => {
        const params = {
            username: getTrimmedNumber(mobile),
            otp,
            productType: AUTH_PRODUCT_TYPE_ME
        };
        setIsLoading(true);
        setOtpError(null);
        verifyOtpConnect(params, selectedCountry).then(async (res) => {
            await saveCookie(COOKIE_KEYS.ACCESS_TOKEN, res?.access_token);
            clearItem(COOKIE_KEYS.CALLBACK_REQUESTES);
            setUserConnect({ ...res });
            handleDealerManagement(params?.username);
        }).catch(error => {
            setOtpError(error?.message || 'Please enter valid OTP.');
            setIsLoading(false);
        });
    };

    const handleSelect = (event) => {
        const { value } = event?.target || {};
        setCountryCode((value || '')?.split(' ')[0]);
    };

    const handleChangeNumber = () => {
        setShowOtp(false);
        setOtpError(false);
    };

    return (
        <Box className="h-full w-full flex flex-col justify-center">
            {isLoading && <PageLoader />}
            <Paper
                sx={{ position: 'fixed', top: 0, left: 0, right: 0, margin: '0 -16px' }}
                elevation={3}
                className="h-20 bg-primary flex flex-col justify-center text-center text-white font-bold text-lg"
            > Login via OTP </Paper>
            {!showOtp && <Card elevation={3} className='rounded-lg'>
                <CardContent>
                    <Typography variant="body1" align="left" className='font-semibold text-lg mb-3'>
                        Select country and enter mobile number to receive OTP
                    </Typography>
                    <FormControl fullWidth size="small" hiddenLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            fullWidth={true}
                            displayEmpty
                            value={countryCode}
                            onChange={handleSelect}
                        >
                            {COUNTRY_CODES?.map(({ code, initial, label }, idx) => (
                                <MenuItem className={`h-12 min-h-full ${(idx + 1) !== COUNTRY_CODES.length && 'border-solid border-b border-v-light-br'}`} key={idx} value={code}><span className='font-bold'>{code}&nbsp;</span>{` - (${initial}-${label})`}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <Input inputType='tel' value={mobile} onChange={handleChange} className="w-full mt-4 mb-2" placeholder="Enter Mobile no." InputProps={{
                            startAdornment: <InputAdornment position="start" className='pr-1 text-border py-6' ><PhoneAndroidIcon /> <span className='pl-1 text-border'>{countryCode}</span></InputAdornment>,
                        }} />
                    </FormControl>
                    <FormControlLabel className='mt-6 mb-1 flex items-baseline' control={<Checkbox defaultChecked checked={whatsappOptIn} onChange={setWhatsappOptIn.bind(null, !whatsappOptIn)} />} label={<Typography variant="body1" className='flex items-start text-base gap-2'><WhatsAppIcon color='success' /> Allow  whatsApp for OTP and all bidding notifications. No promotions!</Typography>} />
                    <Button disabled={!enableSendOtp} variant="contained" color='secondary' fullWidth className='py-3 font-bold text-base' onClick={handleLogin.bind(null, false)}>Get OTP</Button>
                </CardContent>
            </Card>}
            <ConfirmationDialog okBtnText='Ok' message='You have unchecked whatsapp notifications. You will not receive further notifications on whatsapp and you might face difficulties Logging In. Please confirm.' open={showConfirmation} handleClose={setShowConfirmation.bind(null, false)} handleVerified={handleUserConsent} />
            {showOtp && <LoginOtp otpError={otpError} mobile={mobile} handleVerify={handleVerify} handleChangeNumber={handleChangeNumber} handleResendOtp={handleGetOtp.bind(null, true)} />}
        </Box>
    );
};

MobileLogin.propTypes = {
    optIn: PropTypes.bool,
    sendOtpConnect: PropTypes.func,
    verifyOtpConnect: PropTypes.func,
    setupToastConnect: PropTypes.func,
    enlistDealersConnect: PropTypes.func,
    setUserConnect: PropTypes.func,
    getUserConsentConnect: PropTypes.func
};

export default MobileLogin;
